import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarIcon,
  PlusIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  DocumentTextIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const LeaveBalance = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [leaveData, setLeaveData] = useState({});
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('balance');

  useEffect(() => {
    // Sample leave balance and history data
    const data = {
      balance: {
        annual: {
          name: 'Annual Leave',
          total: 21,
          used: 8,
          remaining: 13,
          pendingRequests: 3,
          color: 'blue',
          description: 'Yearly vacation and personal time off'
        },
        sick: {
          name: 'Sick Leave',
          total: 12,
          used: 2,
          remaining: 10,
          pendingRequests: 0,
          color: 'red',
          description: 'Medical leave for illness or health issues'
        },
        casual: {
          name: 'Casual Leave',
          total: 7,
          used: 3,
          remaining: 4,
          pendingRequests: 1,
          color: 'green',
          description: 'Short-term leave for personal matters'
        },
        maternity: {
          name: 'Maternity Leave',
          total: 180,
          used: 0,
          remaining: 180,
          pendingRequests: 0,
          color: 'purple',
          description: 'Maternity and childcare leave'
        },
        paternity: {
          name: 'Paternity Leave',
          total: 15,
          used: 0,
          remaining: 15,
          pendingRequests: 0,
          color: 'orange',
          description: 'Paternity and childcare leave'
        },
        compensatory: {
          name: 'Comp Off',
          total: 5,
          used: 1,
          remaining: 4,
          pendingRequests: 0,
          color: 'indigo',
          description: 'Compensatory time off for overtime work'
        }
      },
      policy: {
        leaveYear: '2025',
        carryForward: 5,
        maxCarryForward: 5,
        encashment: true,
        noticePeriod: {
          annual: 7,
          sick: 0,
          casual: 1
        }
      }
    };

    const history = [
      {
        id: 1,
        type: 'annual',
        startDate: '2025-08-05',
        endDate: '2025-08-07',
        days: 3,
        status: 'approved',
        reason: 'Family vacation',
        appliedDate: '2025-07-15',
        approvedBy: 'Sarah Johnson',
        approvedDate: '2025-07-16'
      },
      {
        id: 2,
        type: 'casual',
        startDate: '2025-08-15',
        endDate: '2025-08-15',
        days: 1,
        status: 'pending',
        reason: 'Personal work',
        appliedDate: '2025-07-20',
        approvedBy: null,
        approvedDate: null
      },
      {
        id: 3,
        type: 'sick',
        startDate: '2025-07-10',
        endDate: '2025-07-11',
        days: 2,
        status: 'approved',
        reason: 'Medical check-up',
        appliedDate: '2025-07-09',
        approvedBy: 'Sarah Johnson',
        approvedDate: '2025-07-09'
      },
      {
        id: 4,
        type: 'annual',
        startDate: '2025-06-20',
        endDate: '2025-06-25',
        days: 5,
        status: 'approved',
        reason: 'Summer vacation',
        appliedDate: '2025-06-01',
        approvedBy: 'Sarah Johnson',
        approvedDate: '2025-06-02'
      },
      {
        id: 5,
        type: 'casual',
        startDate: '2025-05-30',
        endDate: '2025-05-30',
        days: 1,
        status: 'rejected',
        reason: 'Personal work',
        appliedDate: '2025-05-28',
        approvedBy: 'Sarah Johnson',
        approvedDate: '2025-05-29',
        rejectReason: 'Insufficient notice period'
      }
    ];

    setTimeout(() => {
      setLeaveData(data);
      setLeaveHistory(history);
      setLoading(false);
    }, 1000);
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'pending':
        return <ClockIcon className="w-5 h-5 text-yellow-500" />;
      case 'rejected':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      default:
        return <ExclamationTriangleIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const getLeaveTypeColor = (type) => {
    const colors = {
      annual: 'bg-blue-500',
      sick: 'bg-red-500',
      casual: 'bg-green-500',
      maternity: 'bg-purple-500',
      paternity: 'bg-orange-500',
      compensatory: 'bg-indigo-500'
    };
    return colors[type] || 'bg-gray-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-apollo-gray">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-apollo-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Loading leave balance...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      
      {/* Page Header */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white py-6 sm:py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-3 sm:mb-4">
              <CalendarIcon className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 opacity-80" />
            </div>
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-2 sm:mb-4">Leave Balance & Management</h1>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl opacity-90 max-w-2xl mx-auto px-4">
              Manage your leave applications, check balances, and track your time off requests
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation */}
        <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3 lg:gap-4 justify-center lg:justify-start">
          <button
            onClick={() => navigate('/attendance')}
            className="bg-white border border-gray-300 text-gray-700 px-3 sm:px-4 lg:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl hover:bg-gray-50 transition-colors font-medium text-xs sm:text-sm lg:text-base"
          >
            ← Back to Dashboard
          </button>
          <button
            onClick={() => navigate('/attendance/history')}
            className="bg-white border border-gray-300 text-gray-700 px-3 sm:px-4 lg:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl hover:bg-gray-50 transition-colors font-medium text-xs sm:text-sm lg:text-base"
          >
            Attendance History
          </button>
        </div>

        {/* Tabs */}
        <div className="mb-6 sm:mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto scrollbar-hide">
              <button
                onClick={() => setActiveTab('balance')}
                className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'balance'
                    ? 'border-apollo-accent text-apollo-accent'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Leave Balance
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'history'
                    ? 'border-apollo-accent text-apollo-accent'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Leave History
              </button>
              <button
                onClick={() => setActiveTab('policy')}
                className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'policy'
                    ? 'border-apollo-accent text-apollo-accent'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Leave Policy
              </button>
            </nav>
          </div>
        </div>

        {/* Leave Balance Tab */}
        {activeTab === 'balance' && (
          <div className="space-y-6 sm:space-y-8">
            {/* Quick Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
              <div className="bg-white rounded-lg sm:rounded-xl shadow-lg p-3 sm:p-4 lg:p-6 border border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-gray-600 mb-1 truncate">Total Available</p>
                    <p className="text-lg sm:text-xl lg:text-2xl font-bold text-apollo-primary truncate">
                      {Object.values(leaveData.balance || {}).reduce((sum, leave) => sum + leave.remaining, 0)}
                    </p>
                    <p className="text-xs text-gray-500 truncate">days remaining</p>
                  </div>
                  <div className="p-2 sm:p-3 bg-green-100 rounded-full flex-shrink-0">
                    <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-green-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg sm:rounded-xl shadow-lg p-3 sm:p-4 lg:p-6 border border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-gray-600 mb-1 truncate">Used This Year</p>
                    <p className="text-lg sm:text-xl lg:text-2xl font-bold text-apollo-primary truncate">
                      {Object.values(leaveData.balance || {}).reduce((sum, leave) => sum + leave.used, 0)}
                    </p>
                    <p className="text-xs text-gray-500 truncate">days taken</p>
                  </div>
                  <div className="p-2 sm:p-3 bg-blue-100 rounded-full flex-shrink-0">
                    <ChartBarIcon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-blue-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg sm:rounded-xl shadow-lg p-3 sm:p-4 lg:p-6 border border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-gray-600 mb-1 truncate">Pending Requests</p>
                    <p className="text-lg sm:text-xl lg:text-2xl font-bold text-apollo-primary truncate">
                      {Object.values(leaveData.balance || {}).reduce((sum, leave) => sum + leave.pendingRequests, 0)}
                    </p>
                    <p className="text-xs text-gray-500 truncate">awaiting approval</p>
                  </div>
                  <div className="p-2 sm:p-3 bg-yellow-100 rounded-full flex-shrink-0">
                    <ClockIcon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-yellow-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg sm:rounded-xl shadow-lg p-3 sm:p-4 lg:p-6 border border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-gray-600 mb-1 truncate">Leave Year</p>
                    <p className="text-lg sm:text-xl lg:text-2xl font-bold text-apollo-primary truncate">{leaveData.policy?.leaveYear}</p>
                    <p className="text-xs text-gray-500 truncate">current cycle</p>
                  </div>
                  <div className="p-2 sm:p-3 bg-purple-100 rounded-full flex-shrink-0">
                    <DocumentTextIcon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-purple-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* Leave Types */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              {Object.entries(leaveData.balance || {}).map(([key, leave]) => (
                <div key={key} className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 border border-gray-100">
                  <div className="flex items-center justify-between mb-3 sm:mb-4">
                    <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                      <div className={`w-3 h-3 sm:w-4 sm:h-4 rounded-full flex-shrink-0 ${getLeaveTypeColor(key)}`}></div>
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate">{leave.name}</h3>
                    </div>
                    {leave.pendingRequests > 0 && (
                      <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full border border-yellow-200 flex-shrink-0">
                        {leave.pendingRequests} pending
                      </span>
                    )}
                  </div>
                  
                  <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">{leave.description}</p>
                  
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-center justify-between text-xs sm:text-sm">
                      <span className="text-gray-600">Total Allocated</span>
                      <span className="font-semibold text-gray-900">{leave.total} days</span>
                    </div>
                    <div className="flex items-center justify-between text-xs sm:text-sm">
                      <span className="text-gray-600">Used</span>
                      <span className="font-semibold text-red-600">{leave.used} days</span>
                    </div>
                    <div className="flex items-center justify-between text-xs sm:text-sm">
                      <span className="text-gray-600">Remaining</span>
                      <span className="font-semibold text-green-600">{leave.remaining} days</span>
                    </div>
                    
                    <div className="pt-2">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-500">Usage Progress</span>
                        <span className="text-xs text-gray-500">
                          {Math.round((leave.used / leave.total) * 100)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${getLeaveTypeColor(key)}`}
                          style={{ width: `${(leave.used / leave.total) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Apply Leave Button */}
            <div className="text-center">
              <button className="bg-gradient-to-r from-apollo-accent to-blue-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg sm:rounded-xl hover:from-apollo-accent/90 hover:to-blue-600/90 transition-colors font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center space-x-2 mx-auto text-sm sm:text-base">
                <PlusIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Apply for Leave</span>
              </button>
            </div>
          </div>
        )}

        {/* Leave History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-4 sm:space-y-6">
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden">
              <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
                <h3 className="text-base sm:text-lg font-bold text-gray-900">Leave Application History</h3>
              </div>
              
              {/* Mobile Card View */}
              <div className="block md:hidden divide-y divide-gray-200">
                {leaveHistory.map((leave) => (
                  <div key={leave.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-2 min-w-0 flex-1">
                        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${getLeaveTypeColor(leave.type)}`}></div>
                        <h4 className="font-semibold text-gray-900 capitalize text-sm truncate">{leave.type} Leave</h4>
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(leave.status)}`}>
                          {leave.status}
                        </span>
                        {getStatusIcon(leave.status)}
                      </div>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-gray-500">Duration:</span>
                        <div className="font-medium text-gray-900">
                          {new Date(leave.startDate).toLocaleDateString()} - {new Date(leave.endDate).toLocaleDateString()}
                        </div>
                        <div className="text-gray-600">{leave.days} day{leave.days > 1 ? 's' : ''}</div>
                      </div>
                      
                      <div>
                        <span className="text-gray-500">Applied:</span>
                        <div className="font-medium text-gray-900">
                          {new Date(leave.appliedDate).toLocaleDateString()}
                        </div>
                      </div>
                      
                      <div>
                        <span className="text-gray-500">Approver:</span>
                        <div className="font-medium text-gray-900">
                          {leave.approvedBy || 'Pending'}
                        </div>
                        {leave.approvedDate && (
                          <div className="text-gray-600">
                            {new Date(leave.approvedDate).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      
                      <div>
                        <span className="text-gray-500">Reason:</span>
                        <div className="font-medium text-gray-900">{leave.reason}</div>
                        {leave.rejectReason && (
                          <div className="text-red-600 text-xs mt-1">
                            Rejected: {leave.rejectReason}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Desktop Table View */}
              <div className="hidden md:block divide-y divide-gray-200">
                {leaveHistory.map((leave) => (
                  <div key={leave.id} className="p-4 sm:p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between mb-3 sm:mb-4">
                      <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                        <div className={`w-3 h-3 sm:w-4 sm:h-4 rounded-full flex-shrink-0 ${getLeaveTypeColor(leave.type)}`}></div>
                        <h4 className="font-semibold text-gray-900 capitalize text-sm sm:text-base truncate">{leave.type} Leave</h4>
                        <span className={`inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(leave.status)}`}>
                          {leave.status}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        {getStatusIcon(leave.status)}
                        <button className="text-apollo-accent hover:text-apollo-primary transition-colors">
                          <EyeIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 text-xs sm:text-sm">
                      <div>
                        <span className="text-gray-500">Duration:</span>
                        <div className="font-medium text-gray-900">
                          {new Date(leave.startDate).toLocaleDateString()} - {new Date(leave.endDate).toLocaleDateString()}
                        </div>
                        <div className="text-gray-600">{leave.days} day{leave.days > 1 ? 's' : ''}</div>
                      </div>
                      
                      <div>
                        <span className="text-gray-500">Applied:</span>
                        <div className="font-medium text-gray-900">
                          {new Date(leave.appliedDate).toLocaleDateString()}
                        </div>
                      </div>
                      
                      <div>
                        <span className="text-gray-500">Approver:</span>
                        <div className="font-medium text-gray-900">
                          {leave.approvedBy || 'Pending'}
                        </div>
                        {leave.approvedDate && (
                          <div className="text-gray-600">
                            {new Date(leave.approvedDate).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      
                      <div>
                        <span className="text-gray-500">Reason:</span>
                        <div className="font-medium text-gray-900">{leave.reason}</div>
                        {leave.rejectReason && (
                          <div className="text-red-600 text-xs mt-1">
                            Rejected: {leave.rejectReason}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Leave Policy Tab */}
        {activeTab === 'policy' && (
          <div className="space-y-4 sm:space-y-6">
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-4 sm:mb-6">Leave Policy Information</h3>
              
              <div className="space-y-4 sm:space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  <div className="space-y-3 sm:space-y-4">
                    <h4 className="font-semibold text-gray-900 text-sm sm:text-base">General Policy</h4>
                    <div className="space-y-2 text-xs sm:text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Leave Year:</span>
                        <span className="font-medium">{leaveData.policy?.leaveYear}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Carry Forward Limit:</span>
                        <span className="font-medium">{leaveData.policy?.maxCarryForward} days</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Leave Encashment:</span>
                        <span className="font-medium">{leaveData.policy?.encashment ? 'Allowed' : 'Not Allowed'}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3 sm:space-y-4">
                    <h4 className="font-semibold text-gray-900 text-sm sm:text-base">Notice Period Requirements</h4>
                    <div className="space-y-2 text-xs sm:text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Annual Leave:</span>
                        <span className="font-medium">{leaveData.policy?.noticePeriod?.annual} days notice</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Sick Leave:</span>
                        <span className="font-medium">{leaveData.policy?.noticePeriod?.sick || 'Same day'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Casual Leave:</span>
                        <span className="font-medium">{leaveData.policy?.noticePeriod?.casual} day notice</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="pt-4 sm:pt-6 border-t border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-3 sm:mb-4 text-sm sm:text-base">Leave Type Details</h4>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                    {Object.entries(leaveData.balance || {}).map(([key, leave]) => (
                      <div key={key} className="bg-gray-50 rounded-lg p-3 sm:p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <div className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full flex-shrink-0 ${getLeaveTypeColor(key)}`}></div>
                          <h5 className="font-medium text-gray-900 text-xs sm:text-sm">{leave.name}</h5>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-600 mb-2">{leave.description}</p>
                        <div className="text-xs sm:text-sm">
                          <span className="text-gray-500">Annual Allocation: </span>
                          <span className="font-medium">{leave.total} days</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default LeaveBalance;
